import { WILD_SYMBOL, type MapDef, type RegionId, type TerritoryId } from './types';

export interface MapGraph {
  neighbors: Map<TerritoryId, TerritoryId[]>;
  sea: Set<string>;
  regionOf: Map<TerritoryId, RegionId>;
  territoriesOf: Map<RegionId, TerritoryId[]>;
  symbolOf: Map<TerritoryId, string>;
  lairOf: Map<RegionId, TerritoryId[]>;
  ids: TerritoryId[];
  /** Territórios com pelo menos uma rota de barco. */
  ports: Set<TerritoryId>;
  /** Cartas do baralho de território: territórios + curingas. */
  deck: string[];
}

const cache = new WeakMap<MapDef, MapGraph>();

const key = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);

/** Índices pré-calculados do mapa. Cacheado por objeto MapDef. */
export function graphOf(map: MapDef): MapGraph {
  const hit = cache.get(map);
  if (hit) return hit;
  const neighbors = new Map<TerritoryId, TerritoryId[]>();
  const regionOf = new Map<TerritoryId, RegionId>();
  const territoriesOf = new Map<RegionId, TerritoryId[]>();
  const symbolOf = new Map<TerritoryId, string>();
  const lairOf = new Map<RegionId, TerritoryId[]>();
  const sea = new Set<string>();
  for (const r of map.regions) territoriesOf.set(r.id, []);
  for (const t of map.territories) {
    neighbors.set(t.id, []);
    regionOf.set(t.id, t.region);
    symbolOf.set(t.id, t.symbol);
    territoriesOf.get(t.region)?.push(t.id);
  }
  const ports = new Set<TerritoryId>();
  for (const l of map.links) {
    neighbors.get(l.a)?.push(l.b);
    neighbors.get(l.b)?.push(l.a);
    if (l.sea) {
      sea.add(key(l.a, l.b));
      ports.add(l.a);
      ports.add(l.b);
    }
  }
  for (const w of map.wildCards ?? []) symbolOf.set(w, WILD_SYMBOL);
  for (const lair of map.lairs) lairOf.set(lair.region, lair.adjacent);
  const graph: MapGraph = {
    neighbors,
    sea,
    regionOf,
    territoriesOf,
    symbolOf,
    lairOf,
    ids: map.territories.map((t) => t.id),
    ports,
    deck: [...map.territories.map((t) => t.id), ...(map.wildCards ?? [])],
  };
  cache.set(map, graph);
  return graph;
}

export function areAdjacent(map: MapDef, a: TerritoryId, b: TerritoryId): boolean {
  return graphOf(map).neighbors.get(a)?.includes(b) ?? false;
}

export function linkKey(a: string, b: string): string {
  return key(a, b);
}

export function isSeaLink(map: MapDef, a: TerritoryId, b: TerritoryId): boolean {
  return graphOf(map).sea.has(key(a, b));
}

/** Valida a consistência de um MapDef. Retorna a lista de problemas. */
export function validateMap(map: MapDef): string[] {
  const problems: string[] = [];
  const ids = new Set(map.territories.map((t) => t.id));
  const regions = new Set(map.regions.map((r) => r.id));
  for (const t of map.territories) {
    if (!regions.has(t.region)) problems.push(`território ${t.id}: região ${t.region} desconhecida`);
  }
  for (const l of map.links) {
    if (!ids.has(l.a) || !ids.has(l.b)) problems.push(`ligação ${l.a}-${l.b}: território desconhecido`);
  }
  for (const lair of map.lairs) {
    if (!regions.has(lair.region)) problems.push(`covil: região ${lair.region} desconhecida`);
    for (const a of lair.adjacent) if (!ids.has(a)) problems.push(`covil ${lair.region}: ${a} desconhecido`);
  }
  for (const r of map.regionDie) if (!regions.has(r)) problems.push(`dado de região: ${r} desconhecida`);
  // conectividade
  const g = graphOf(map);
  const start = map.territories[0]?.id;
  if (start) {
    const seen = new Set([start]);
    const queue = [start];
    while (queue.length) {
      const cur = queue.shift()!;
      for (const n of g.neighbors.get(cur) ?? []) {
        if (!seen.has(n)) {
          seen.add(n);
          queue.push(n);
        }
      }
    }
    if (seen.size !== ids.size) problems.push('mapa desconexo');
  }
  return problems;
}
